'use client';

/**
 * useEmbedReturn — the way back to the page a visitor arrived from.
 *
 * When someone taps expand ↗ in the embedded panel, the loader navigates the
 * host tab to `/us-en/ai-assistant?c=…&from=<ctx>`. Expand is a ROUND TRIP,
 * not an exit (design 21c): without a way back, a paid visitor who wanted a
 * bigger window is simply gone from the registration page that was paying to
 * reach them.
 *
 * The chip persists for the rest of the SESSION, not just the first render —
 * a "New chat" click, a history switch, or any later URL rewrite must not
 * silently strip it. The URL param is the source of truth on arrival; after
 * that sessionStorage carries it, which is also what makes it survive a
 * reload of the assistant page.
 *
 * Returns null when there is no resolvable origin — a chip pointing at a
 * destination we guessed would be worse than no chip.
 */

import { useEffect, useSyncExternalStore } from 'react';
import { useSearchParams } from 'next/navigation';
import { EMBED_CONTEXTS } from '@/lib/embed-context';

const STORAGE_KEY = 'sara.embed.from';

export interface EmbedReturn {
  url: string;
  label: string;
}

/** Client snapshot. Returns a primitive, so it is stable across renders. */
function read(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // Private mode / blocked storage — the URL param still covers the
    // first render, which is the case that matters most.
    return null;
  }
}

/** Server snapshot — there is no sessionStorage during SSR. */
function getServerSnapshot(): string | null {
  return null;
}

/** See the note at the call site for why this never needs to fire. */
function subscribeToNothing(): () => void {
  return () => {};
}

function persist(key: string): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, key);
  } catch {
    /* nothing sensible to do; the in-memory state still serves this page */
  }
}

export function useEmbedReturn(): EmbedReturn | null {
  const searchParams = useSearchParams();
  const fromParam = searchParams.get('from');

  // The param is used DIRECTLY in the derivation below rather than mirrored
  // into state — so a visitor arriving with ?from= gets the chip in the very
  // first paint, with no state round-trip and nothing to keep in sync.
  //
  // The sessionStorage fallback is a genuine external store, so it uses
  // useSyncExternalStore rather than a read-into-state effect: that keeps SSR
  // correct via the server snapshot, and avoids the cascading re-render that
  // setState-inside-an-effect would schedule on every mount.
  //
  // `subscribe` is intentionally a no-op. sessionStorage emits `storage`
  // events only for OTHER tabs, and the only writer of this key is us — at
  // which point `fromParam` is set and takes precedence anyway. There is no
  // change we could miss.
  const storedKey = useSyncExternalStore(
    subscribeToNothing,
    read,
    getServerSnapshot
  );

  // Persist on arrival so the chip survives reloads and later URL rewrites.
  // No setState here — this effect is pure side effect.
  useEffect(() => {
    if (fromParam) persist(fromParam);
  }, [fromParam]);

  const key = fromParam ?? storedKey;
  if (!key) return null;
  const context = EMBED_CONTEXTS[key];
  if (!context?.returnUrl || !context.returnLabel) return null;
  return { url: context.returnUrl, label: context.returnLabel };
}
